import type { Query, QueryClient } from "@tanstack/react-query";

const PERSON_QUERY_AREAS = new Set(["person", "persons"]);
const CHARGEBACK_TASK_QUERY_AREA = "chargeback-task";
const LIST_AREAS = new Set(["settings-list", "settings-list-rows", "settings-list-scope-widen"]);
const FINANCE_LISTS = new Set(["open-items", "open-items-grouped", "article-sales-history", "bank-statement-assignments", "debtCollectionCases", "debtCollectionCasesByPerson", "tasks"]);
const PERSON_DATA = new Set(["detail", "overview", "payments", "contributions", "open-items", "wallets", "check-in-eligibility", "aggregator-requirements", "contract-packages", "contract-blocks", "contract-targets", "rest-periods"]);

/** One post-commit read plan for finance and contract effects, including active list and inner fetchQuery caches. */
export async function refreshFinanceData(client: QueryClient, personId: string, { throwOnError = true } = {}) {
  const predicate = (query: Query) => {
    const [area, key, data] = query.queryKey;
    if (PERSON_QUERY_AREAS.has(String(area)) && key === personId && (data === undefined || PERSON_DATA.has(String(data)))) return true;
    if (area === CHARGEBACK_TASK_QUERY_AREA && (query.state.data as { personId?: string } | undefined)?.personId === personId) return true;
    return LIST_AREAS.has(String(area)) && typeof key === "string"
      && (key.startsWith("persons.detail.") || FINANCE_LISTS.has(key) || key.startsWith("m1member.debt-collection-cases-page"));
  };
  await client.invalidateQueries({ predicate, refetchType: "none" });
  const active = client.getQueryCache().findAll({ predicate, type: "active" });
  await Promise.allSettled(active.map(query => client.refetchQueries(
    { queryKey: query.queryKey, exact: true, type: "active" }, { cancelRefetch: true, throwOnError: true }
  )));
  // Another committed operation may replace a shared list request. A cancelled promise is not a fresh snapshot.
  let pending = active.filter(query => query.state.fetchStatus === "fetching" && query.promise);
  while (pending.length) {
    await Promise.allSettled(pending.map(query => query.promise));
    pending = active.filter(query => query.state.fetchStatus === "fetching" && query.promise);
  }
  if (!throwOnError) return;
  const failure = active.find(query => query.state.status === "error");
  if (failure) throw failure.state.error;
  const failedSnapshot = active.find(query => LIST_AREAS.has(String(query.queryKey[0])) && Boolean((query.state.data as { requestError?: string } | undefined)?.requestError));
  if (failedSnapshot) throw new Error((failedSnapshot.state.data as { requestError: string }).requestError);
  if (active.some(query => query.state.fetchStatus !== "idle" || query.state.isInvalidated)) throw new Error("Finance refresh incomplete");
}
